import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function FiscalConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiscal/config");
      setCfg(data);
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        regime: cfg.regime,
        csosn_default: cfg.csosn_default,
        nfe_series: Number(cfg.nfe_series) || 1,
        ncm_default: (cfg.ncm_default || "").replace(/\D/g, ""),
        emitter_uf: (cfg.emitter_uf || "SP").toUpperCase(),
        natureza_operacao_id: cfg.natureza_operacao_id ? Number(cfg.natureza_operacao_id) : null,
        default_email: cfg.default_email || "",
        default_email_message: cfg.default_email_message || "",
      };
      await api.put("/fiscal/config", payload);
      toast.success("Configurações salvas");
      await load();
    } catch (e) {
      toast.error("Falha ao salvar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) return <div className="text-center text-zinc-400 py-8">Carregando…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="max-w-3xl space-y-6">
      <Section title="Regime Tributário" subtitle="A fábrica opera em Simples Nacional (CSOSN em vez de CST)">
        <Row>
          <Field label="Regime">
            <select value={cfg.regime} onChange={(e) => set("regime", e.target.value)} className="border border-black px-3 py-2 w-full" data-testid="fiscal-cfg-regime">
              <option value="simples_nacional">Simples Nacional</option>
              <option value="regime_normal" disabled>Regime Normal (não suportado)</option>
            </select>
          </Field>
          <Field label="CSOSN padrão">
            <input value={cfg.csosn_default || ""} onChange={(e) => set("csosn_default", e.target.value)} className="border border-black px-3 py-2 w-full font-mono" data-testid="fiscal-cfg-csosn" />
          </Field>
        </Row>
      </Section>

      <Section title="Emissão" subtitle="Padrões aplicados a toda NF-e. CFOP é calculado automaticamente por UF.">
        <Row>
          <Field label="Série NF-e">
            <input type="number" min={1} value={cfg.nfe_series} onChange={(e) => set("nfe_series", e.target.value)} className="border border-black px-3 py-2 w-full font-mono" data-testid="fiscal-cfg-series" />
          </Field>
          <Field label="NCM padrão">
            <input value={cfg.ncm_default || ""} onChange={(e) => set("ncm_default", e.target.value)} className="border border-black px-3 py-2 w-full font-mono" placeholder="39241000" data-testid="fiscal-cfg-ncm" />
          </Field>
          <Field label="UF do emissor">
            <select value={cfg.emitter_uf} onChange={(e) => set("emitter_uf", e.target.value)} className="border border-black px-3 py-2 w-full" data-testid="fiscal-cfg-uf">
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="ID Natureza da Operação (Bling)" hint="Opcional — se sua conta Bling exigir uma natureza de operação específica, cole o ID aqui.">
            <input value={cfg.natureza_operacao_id || ""} onChange={(e) => set("natureza_operacao_id", e.target.value)} className="border border-black px-3 py-2 w-full font-mono" placeholder="ex.: 12345" data-testid="fiscal-cfg-natureza" />
          </Field>
        </Row>
        <div className="text-xs bg-zinc-50 border border-zinc-200 p-3">
          <b>CFOP automático:</b> 5102 (dentro de {cfg.emitter_uf}) ou 6102 (fora de {cfg.emitter_uf}), com base no endereço do cliente do pedido.
        </div>
      </Section>

      <Section title="E-mail padrão" subtitle="Usado quando o pedido não tem e-mail do cliente ou você deixa o campo em branco no envio.">
        <Field label="E-mail padrão">
          <input type="email" value={cfg.default_email || ""} onChange={(e) => set("default_email", e.target.value)} className="border border-black px-3 py-2 w-full" placeholder="fiscal@popcopos.com.br" data-testid="fiscal-cfg-email" />
        </Field>
        <Field label="Mensagem padrão">
          <textarea rows={3} value={cfg.default_email_message || ""} onChange={(e) => set("default_email_message", e.target.value)} className="border border-black px-3 py-2 w-full text-sm" data-testid="fiscal-cfg-message" />
        </Field>
      </Section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-black text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50" data-testid="fiscal-cfg-save">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Salvando…" : "Salvar configurações"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="border border-black bg-white">
      <header className="border-b border-black px-4 py-3">
        <div className="font-head font-black text-base">{title}</div>
        {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
}
function Field({ label, hint, children }) {
  return (
    <div>
      <div className="overline text-[10px] text-zinc-500 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
